import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { mockMovies, categories } from '../../data/mockData';
import apiService from '../../services/apiService';

// Async thunks for API calls
export const fetchTrendingContent = createAsyncThunk(
  'content/fetchTrending',
  async () => {
    const content = await apiService.getTrendingContent();
    return apiService.transformContentForNetflix(content);
  }
);

export const fetchMovies = createAsyncThunk(
  'content/fetchMovies',
  async () => {
    const content = await apiService.getLatestMovies();
    return apiService.transformContentForNetflix(content);
  }
);

export const fetchTVShows = createAsyncThunk(
  'content/fetchTVShows',
  async () => {
    const content = await apiService.getLatestTVShows();
    return apiService.transformContentForNetflix(content);
  }
);

export const searchContent = createAsyncThunk(
  'content/search',
  async (query) => {
    const content = await apiService.searchContent(query);
    return apiService.transformContentForNetflix(content);
  }
);

export const fetchContentByCategory = createAsyncThunk(
  'content/fetchByCategory',
  async (category) => {
    const content = await apiService.getContentByCategory(category);
    return apiService.transformContentForNetflix(content);
  }
);

// Fetch content from Firebase (fast!)
export const fetchFirebaseContent = createAsyncThunk(
  'content/fetchFromFirebase',
  async (limit = 200) => {
    const response = await apiService.getFirebaseMovies(limit);
    const firebaseMovies = response.movies;
    const transformed = apiService.transformFirebaseToNetflix(firebaseMovies);

    console.log(`[fetchFirebaseContent] Fetched ${transformed.length} movies from Firebase`);

    return transformed;
  }
);

const initialState = {
  allContent: mockMovies, // Keep as fallback
  categories: categories,
  scrapedContent: {
    trending: [],
    movies: [],
    tvShows: [],
  },
  searchResults: [],
  searchQuery: '',
  currentlyPlaying: null,
  isLoading: false,
  error: null,
  apiHealthy: false,
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = '';
      state.searchResults = [];
    },
    setCurrentlyPlaying: (state, action) => {
      state.currentlyPlaying = action.payload;
    },
    clearCurrentlyPlaying: (state) => {
      state.currentlyPlaying = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateContentRating: (state, action) => {
      const { contentId, rating } = action.payload;
      const content = state.allContent.find(item => item.id === contentId);
      if (content) {
        content.userRating = rating;
      }
    },
    setApiHealthy: (state, action) => {
      state.apiHealthy = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Trending content
    builder
      .addCase(fetchTrendingContent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrendingContent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scrapedContent.trending = action.payload;
        state.apiHealthy = true;
      })
      .addCase(fetchTrendingContent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      })

    // Movies
      .addCase(fetchMovies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMovies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scrapedContent.movies = action.payload;
        state.apiHealthy = true;
      })
      .addCase(fetchMovies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      })

    // TV Shows
      .addCase(fetchTVShows.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTVShows.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scrapedContent.tvShows = action.payload;
        state.apiHealthy = true;
      })
      .addCase(fetchTVShows.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      })

    // Search
      .addCase(searchContent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchContent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
        state.apiHealthy = true;
      })
      .addCase(searchContent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      })

    // Category fetch
      .addCase(fetchContentByCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContentByCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiHealthy = true;
      })
      .addCase(fetchContentByCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      })

    // Firebase content fetch
      .addCase(fetchFirebaseContent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFirebaseContent.fulfilled, (state, action) => {
        state.isLoading = false;
        // Store Firebase data in movies array (used by all homepage sections)
        state.scrapedContent.movies = action.payload;
        state.apiHealthy = true;
      })
      .addCase(fetchFirebaseContent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.apiHealthy = false;
      });
  },
});

export const {
  setSearchQuery,
  setSearchResults,
  clearSearch,
  setCurrentlyPlaying,
  clearCurrentlyPlaying,
  setLoading,
  setError,
  clearError,
  updateContentRating,
  setApiHealthy,
} = contentSlice.actions;

export default contentSlice.reducer;